/*
Copyright 2020 Adobe
All Rights Reserved.
NOTICE: Adobe permits you to use, modify, and distribute this file in
accordance with the terms of the Adobe license agreement accompanying
it. If you have received this file from a source other than Adobe,
then your use, modification, or distribution of it requires the prior
written permission of Adobe.
*/

import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { v4 } from 'uuid';

export type PreviewEvent = {type: 'download' | 'start' | 'rendering', progress?: number}

@Injectable({
  providedIn: 'root'
})
export class ViewSDKClient {

  constructor(private http: HttpClient) {
  }

  readyPromise: Promise<any> = new Promise<void>((resolve) => {
    // @ts-ignore
    if (window['AdobeDC']) {
      resolve();
    } else {
      /* Wait for Adobe Acrobat Services PDF Embed API to be ready */
      document.addEventListener('adobe_dc_view_sdk.ready', () => {
        resolve();
      });
    }
  });

  ready() {
    return this.readyPromise;
  }

  preview(url: string, viewerConfig: any = {}): Observable<PreviewEvent> {
    const subject = new BehaviorSubject<PreviewEvent>({type: 'start'});
    this.http.get(url, {reportProgress: true, responseType: 'arraybuffer', observe: 'events' })
      .subscribe(event => {
        switch (event.type) {
          case HttpEventType.DownloadProgress: {
              subject.next({type: 'download', progress: Math.floor(event.loaded / event.total! * 100)})
            break;
          }
          case HttpEventType.Response: {
            if (!event.ok) {
              subject.error(event.statusText);
              return;
            }
            const config: any = {
              /* Pass your registered client id */
              clientId: environment.adobeKey,
              divId: 'pdf-div',
              locale: "fr-FR",
              showSimpleLoader: true
            };
            viewerConfig.embedMode = 'SIZED_CONTAINER';
            viewerConfig.showAnnotationTools = false;
            viewerConfig.showFullScreen = false;
            /* Initialize the AdobeDC View object */
            // @ts-ignore
            const adobeDCView = new window['AdobeDC'].View(config);
            adobeDCView.registerCallback('EVENT_LISTENER', (event: { type: any; }) => {
              switch (event.type) {
                case 'APP_RENDERING_START': {
                  subject.next({type: 'rendering'});
                  break;
                }
                case 'APP_RENDERING_DONE': {
                  subject.complete();
                  break;
                }
                case 'APP_RENDERING_FAILED': {
                  subject.error('pdf.rendering.failed');
                  break;
                }
              }
            });

            /* Invoke the file preview API on Adobe DC View object */
            adobeDCView.previewFile({
              content: {
                promise: Promise.resolve(event.body),
              },
              metaData: {
                fileName: 'document.pdf',
                id: v4(),
                hasReadOnlyAccess: true
              },
            }, viewerConfig);
            break;
          }
        }
      });
    return subject;

  }
}
